import { useCallback, useMemo, useState } from 'react';

import { capitalize, cloneDeep } from 'lodash';
import * as yup from 'yup';

import { typedAssign } from 'utils/object';
import { getValidationErrorsSync } from 'utils/Validators';

import { EditVertex } from '../../FlowchartEditor';
import {
  TableColumn,
  FlowchartQueryModel,
  FlowchartVertex,
  Join,
  getVertex,
  SourceTable,
  DestinationSocketType,
} from '../../model/FlowchartQueryModel';
import { getAvailableColumnValuesFromSocket } from '../../query_builder/availableColumns';
import { aliasDuplicateColumns } from '../../query_builder/columnDeduplicator';

import { validateColumnSelections } from '../select_columns/SelectColumnsModal/ColumnSelector/useColumnSelector';

import { JoinModalProps, SavableJoinProps } from './JoinModal';

export interface JoinState extends EditVertex<Join, JoinModalProps> {}

export default function useJoin(
  fqm: FlowchartQueryModel,
  updateVertex: (vertex: FlowchartVertex) => void,
  handleSelectVertex: (vertex: FlowchartVertex) => void,
): JoinState {
  const [vertex, setVertex] = useState<Join | null>(null);

  const onCancel = useCallback(() => {
    setVertex(null);
  }, []);

  const onSave = useCallback(
    (savableProps: SavableJoinProps) => {
      if (vertex) {
        const updatedVertex = cloneDeep(vertex);
        typedAssign(updatedVertex, savableProps);

        updateVertex(updatedVertex);
        handleSelectVertex(updatedVertex);
        setVertex(null);
      }
    },
    [vertex, updateVertex, handleSelectVertex],
  );

  const onEditVertex = useCallback((vertex: Join) => {
    setVertex(vertex);
  }, []);

  const leftJoinDisplayName = useMemo(() => getJoinDisplayName(fqm, vertex, 'left'), [fqm, vertex]);
  const rightJoinDisplayName = useMemo(() => getJoinDisplayName(fqm, vertex, 'right'), [fqm, vertex]);

  const leftAvailableColumnValues = useMemo(
    () => getAvailableColumnValuesFromSocket(fqm, vertex, 'left'),
    [fqm, vertex],
  );
  const rightAvailableColumnValues = useMemo(
    () => getAvailableColumnValuesFromSocket(fqm, vertex, 'right'),
    [fqm, vertex],
  );

  const availableColumnValues = useMemo(
    () => [...leftAvailableColumnValues, ...rightAvailableColumnValues],
    [leftAvailableColumnValues, rightAvailableColumnValues],
  );

  const initialSelectedColumns: TableColumn[] | undefined = useMemo(() => {
    // If the user previously saved selectedColumns, use that.
    if (vertex?.selectedColumns) {
      return vertex.selectedColumns;
    }

    // If we had to automatically deduplicate a column name,
    // return the deduplicated column list.
    const deduplicatedColumns = aliasDuplicateColumns(availableColumnValues);
    const hasDeduplicate = deduplicatedColumns.some((c) => !!c.alias);
    if (hasDeduplicate) {
      return deduplicatedColumns;
    }

    // Default case
    return undefined;
  }, [vertex?.selectedColumns, availableColumnValues]);

  const modalProps: JoinModalProps | null = useMemo(() => {
    if (vertex) {
      const joinModalProps: JoinModalProps = {
        joinType: vertex.joinType,
        leftColumn: vertex.leftColumn,
        rightColumn: vertex.rightColumn,
        leftJoinDisplayName,
        rightJoinDisplayName,
        leftJoinOnColumns: leftAvailableColumnValues,
        rightJoinOnColumns: rightAvailableColumnValues,
        availableColumnValues,
        selectedColumnValues: initialSelectedColumns,
        onCancel,
        onSave,
      };

      return joinModalProps;
    }

    return null;
  }, [
    vertex,
    leftJoinDisplayName,
    rightJoinDisplayName,
    leftAvailableColumnValues,
    rightAvailableColumnValues,
    availableColumnValues,
    initialSelectedColumns,
    onCancel,
    onSave,
  ]);

  const result: JoinState = useMemo(
    () => ({
      modalProps,
      onEditVertex,
    }),
    [modalProps, onEditVertex],
  );

  return result;
}

export const validateJoin = (
  leftColumn: string,
  rightColumn: string,
  currentSelections?: TableColumn[],
): [Record<string, string>, Record<string, string>, string] => {
  const [aliasErrors, formError] = validateColumnSelections(currentSelections);
  // MAYBE TODO:
  // Check that leftColumn and rightColumn are valid column names
  const schema = yup.object({
    leftColumn: yup.string().required('Required'),
    rightColumn: yup.string().required('Required'),
  });
  const formValues = {
    leftColumn,
    rightColumn,
  };
  const joinErrors = getValidationErrorsSync(schema, formValues);
  return [joinErrors, aliasErrors, formError];
};

export function getJoinDisplayName(
  fqm: FlowchartQueryModel,
  vertex: Join | null,
  socket: DestinationSocketType,
) {
  let displayName = capitalize(socket);
  if (vertex) {
    const parentID = vertex.socketToParentID(socket) || 'UNKNOWN';
    const parent = getVertex(fqm, parentID);

    displayName = `${displayName} ${parent.prettyType()}`;

    if (parent.type === 'source_table') {
      displayName = (parent as SourceTable).table?.full_name || displayName;
    }
  }
  return displayName;
}
